/**
 * A module's tiles, fetched.
 *
 * The playlist says *which* capsules and in what order; everything a tile shows
 * comes from the posts they were published as, and those arrive in one batched
 * request — the same one the classroom's grid and a profile's tab already use to
 * draw work from a list of addresses (`useClassroomPostViews`).
 *
 * The hook exists so the three places that draw a playlist do not each write the
 * same four lines: which post addresses to ask for, what to do while they are
 * still on their way, and how a place with no post at all is named. The last one
 * is the interesting one and it is `#/lib/moduleTiles`' whole job — a place is
 * *loading* while the request is out and *missing* once it has settled, and a
 * studio passes what it already knows about the capsule (its record, and whether
 * it is a draft) so those two states are told apart without a request at all.
 */
import {useMemo} from 'react'

import {type ModuleItem} from '#/lib/modulePlaylist'
import {
  moduleTiles,
  type ModuleTile,
  type ModuleTileFacts,
} from '#/lib/moduleTiles'
import {useClassroomPostViews} from '#/state/queries/classroom-posts'

export function useModuleTiles(
  items: readonly ModuleItem[],
  /**
   * What the caller already knows about the capsules.
   *
   * The studio passes the creator's own records, which is how a tile knows it is
   * a *draft* rather than a capsule that has gone; a card in a feed passes
   * nothing, because a published module cannot hold a capsule without a post.
   */
  capsules?: readonly ModuleTileFacts[],
): {tiles: ModuleTile[]; isPending: boolean} {
  const postUris = useMemo(
    () => items.map(item => item.postUri).filter((uri): uri is string => !!uri),
    [items],
  )
  const {data: posts, isPending} = useClassroomPostViews(postUris)

  const tiles = useMemo(
    () => moduleTiles({items, capsules, posts, isPending}),
    [capsules, isPending, items, posts],
  )

  return {tiles, isPending}
}
